import { useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router';
import { ArrowLeft, Calendar, Info, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import { FeatureGate } from '@/components/features/FeatureGate';
import { ShoppingListsLockedPage } from '@/components/features/FeatureLockedPage';
import { Feature } from '@/types/auth';
import { todayLocalDateStr } from '@/lib/date';
import {
    displayUnit,
    formatListRange,
    groupShoppingItems,
    shortWeekday,
} from '@/lib/shoppingItems';
import { cn } from '@/lib/utils';
import { addDaysToListPath } from '@/lib/planShopping';
import { ShoppingItemRow } from '@/components/shopping/ShoppingItemRow';
import { AddOwnItemForm } from '@/components/shopping/AddOwnItemForm';
import { ListTitle } from '@/components/shopping/ListTitle';
import {
    ADDED_BY_YOU_SECTION,
    useAddShoppingItem,
    useDeleteShoppingItem,
    usePlanDatesForShopping,
    useRenameShoppingList,
    useShoppingList,
    useTickShoppingItem,
    useUpdateShoppingItem,
    type ShoppingListItem,
    type ShoppingListRouteState,
    type UnlistedMeal,
    type UpdateShoppingItemPayload,
} from '@/services/shoppingLists';

/** "Braai (Sat), Eating out (Fri)". */
function describeMeals(meals: UnlistedMeal[]): string {
    return meals
        .map((meal) => {
            const date = meal.date?.slice(0, 10);
            return date ? `${meal.name} (${shortWeekday(date)})` : meal.name;
        })
        .join(', ');
}

const statusOf = (error: unknown) =>
    (error as { response?: { status?: number } })?.response?.status;

function serverMessage(error: unknown, fallback: string): string {
    const message = (error as { response?: { data?: { message?: unknown } } })?.response?.data
        ?.message;
    if (typeof message === 'string' && message.trim()) return message;
    if (Array.isArray(message) && typeof message[0] === 'string') return message[0];
    return fallback;
}

export function ShoppingListDetailContent() {
    const { id = '' } = useParams<{ id: string }>();
    const location = useLocation();
    const routeState = (location.state ?? {}) as ShoppingListRouteState;
    const unlistedMeals = routeState.unlistedMeals ?? [];
    const warnings = routeState.warnings ?? [];

    const { data: list, isLoading, isError } = useShoppingList(id);
    // Always asked for: it labels the list's days and decides "+ Add days".
    const { data: plan } = usePlanDatesForShopping(todayLocalDateStr());

    const tick = useTickShoppingItem(id);
    const add = useAddShoppingItem(id);
    const update = useUpdateShoppingItem(id);
    const remove = useDeleteShoppingItem(id);
    const rename = useRenameShoppingList(id);
    const [editingId, setEditingId] = useState<string | null>(null);

    const sections = useMemo(() => groupShoppingItems(list?.items ?? []), [list?.items]);
    const writing = update.isPending || remove.isPending;

    if (isLoading) {
        return (
            <div className="mx-auto max-w-5xl px-4 py-8 lg:px-8">
                <p className="text-muted-foreground">Loading your list...</p>
            </div>
        );
    }

    if (isError || !list) {
        return (
            <div className="mx-auto max-w-5xl px-4 py-8 lg:px-8">
                <BackLink />
                <p className="mt-4 text-lg text-foreground">We couldn't find this list.</p>
            </div>
        );
    }

    const total = list.items.length;
    const ticked = list.items.filter((item) => item.isPurchased).length;
    const toGo = total - ticked;
    const percent = total ? Math.round((ticked / total) * 100) : 0;
    const range = formatListRange(list, plan);
    const canAddDays = !!plan && plan.phase !== 'FINISHED' && !list.isCompleted;

    const handleTick = (item: ShoppingListItem) => {
        tick.mutate(
            { itemId: item.id, isPurchased: !item.isPurchased },
            {
                onError: () =>
                    toast.error(`Couldn't save the tick on ${item.displayName}. Try again.`),
            },
        );
    };

    const handleSave = (item: ShoppingListItem, change: UpdateShoppingItemPayload) => {
        update.mutate(
            { itemId: item.id, ...change },
            {
                onSuccess: () => setEditingId(null),
                onError: (error) => {
                    // 409: the list already has this ingredient in the unit typed,
                    // and two rows for one line are not allowed (#376).
                    if (statusOf(error) === 409 && change.unit !== undefined) {
                        const unit = displayUnit(change.unit);
                        toast.error(
                            unit
                                ? `This list already has a ${item.displayName} line in "${unit}". Change that line instead, or use another unit.`
                                : `This list already has a ${item.displayName} line with no unit. Change that line instead, or add a unit.`,
                        );
                        return;
                    }
                    toast.error(serverMessage(error, "Couldn't save that change."));
                },
            },
        );
    };

    const handleRemove = (item: ShoppingListItem) => {
        remove.mutate(item.id, {
            onSuccess: () => {
                if (editingId === item.id) setEditingId(null);
                toast.success(`Removed ${item.displayName}`);
            },
            onError: () => toast.error(`Couldn't remove ${item.displayName}.`),
        });
    };

    const handleAdd = (item: { name: string; amount: number | null; unit: string }) =>
        add
            .mutateAsync({
                name: item.name,
                ...(item.amount !== null ? { amount: item.amount } : {}),
                ...(item.unit ? { unit: item.unit } : {}),
            })
            .catch((error) => {
                toast.error(serverMessage(error, `Couldn't add ${item.name}.`));
                throw error;
            });

    return (
        <div className="mx-auto max-w-5xl px-4 py-6 lg:px-8 lg:py-8">
            <BackLink />
            <header className="mt-3 mb-5 flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <ListTitle
                        name={list.name}
                        saving={rename.isPending}
                        onRename={(name) =>
                            rename.mutateAsync(name).catch((error) => {
                                toast.error(serverMessage(error, "Couldn't rename the list."));
                                throw error;
                            })
                        }
                    />
                    {range && (
                        <p className="mt-1 flex items-center gap-1.5 text-base text-muted-foreground">
                            <Calendar className="size-4 shrink-0" aria-hidden="true" />
                            {range}
                        </p>
                    )}
                </div>
                {canAddDays && (
                    <Link
                        to={addDaysToListPath(list.id)}
                        aria-label="Add days"
                        className="mt-1 inline-flex h-10 shrink-0 items-center gap-1.5 rounded-lg border border-brand-surface-edge bg-brand-surface px-3 text-sm font-medium text-foreground shadow-sm hover:bg-brand-header-wash focus-visible:outline-2 focus-visible:outline-brand-header sm:px-4"
                    >
                        <Plus className="size-4" aria-hidden="true" />
                        <span className="sm:hidden">Days</span>
                        <span className="hidden sm:inline">Add days</span>
                    </Link>
                )}
            </header>

            <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_22rem] lg:items-start lg:gap-8">
                {/* On a phone the progress, add form and notes come first; on a
                    wide screen they sit in a column beside the list. */}
                <aside className="space-y-4 lg:sticky lg:top-24 lg:order-2">
                    <section
                        aria-label="Progress"
                        className="rounded-xl border border-brand-surface-edge bg-brand-surface p-4 shadow-sm"
                    >
                        <div className="flex items-baseline justify-between gap-3">
                            <p className="text-lg font-bold text-foreground">
                                {ticked} of {total} ticked off
                            </p>
                            <p className="text-sm text-muted-foreground">
                                {total > 0 && toGo === 0 ? 'All done' : `${toGo} to go`}
                            </p>
                        </div>
                        <div
                            role="progressbar"
                            aria-label="Ticked off"
                            aria-valuemin={0}
                            aria-valuemax={total}
                            aria-valuenow={ticked}
                            className="mt-3 h-3 overflow-hidden rounded-full bg-brand-bar-track"
                        >
                            <div
                                className="h-full rounded-full border border-brand-bar-edge bg-brand-bar-fill transition-[width] duration-300"
                                style={{
                                    width: `${percent}%`,
                                    display: ticked ? undefined : 'none',
                                }}
                            />
                        </div>
                    </section>

                    <AddOwnItemForm pending={add.isPending} onAdd={handleAdd} />

                    {unlistedMeals.length > 0 && (
                        <p className="flex gap-3 rounded-xl bg-brand-header-wash p-4 text-sm text-foreground">
                            <Info
                                className="mt-0.5 size-5 shrink-0 text-brand-text-blue"
                                aria-hidden="true"
                            />
                            <span>
                                <strong>
                                    {unlistedMeals.length === 1
                                        ? "1 meal isn't on this list:"
                                        : `${unlistedMeals.length} meals aren't on this list:`}
                                </strong>{' '}
                                {describeMeals(unlistedMeals)}.{' '}
                                {unlistedMeals.length === 1 ? 'It has' : 'They have'} no recipe, so
                                add what you need yourself.
                            </span>
                        </p>
                    )}

                    {warnings.map((warning) => (
                        <p
                            key={warning.code}
                            className="flex gap-3 rounded-xl bg-brand-header-wash p-4 text-sm text-foreground"
                        >
                            <Info
                                className="mt-0.5 size-5 shrink-0 text-brand-text-blue"
                                aria-hidden="true"
                            />
                            <span>{warning.message}</span>
                        </p>
                    ))}
                </aside>

                <div className="space-y-6 lg:order-1">
                    {sections.length === 0 && (
                        <p className="rounded-xl border border-brand-surface-edge bg-brand-surface p-6 text-center text-muted-foreground">
                            Nothing on this list yet. Add your own item to start.
                        </p>
                    )}
                    {sections.map((group) => {
                        const headingId = `section-${group.section.replace(/\W+/g, '-')}`;
                        return (
                            <section key={group.section} aria-labelledby={headingId}>
                                <div className="mb-2 flex items-baseline justify-between px-1">
                                    <h2
                                        id={headingId}
                                        className={cn(
                                            'text-xs font-bold uppercase tracking-[0.12em]',
                                            group.section === ADDED_BY_YOU_SECTION
                                                ? 'text-brand-text-blue'
                                                : 'text-brand-text-green',
                                        )}
                                    >
                                        {group.section}
                                    </h2>
                                    <span className="text-xs text-muted-foreground">
                                        {group.ticked} of {group.items.length}
                                    </span>
                                </div>
                                <ul className="divide-y divide-brand-surface-edge overflow-hidden rounded-xl border border-brand-surface-edge bg-brand-surface shadow-sm">
                                    {group.items.map((item) => (
                                        <ShoppingItemRow
                                            key={item.id}
                                            item={item}
                                            editing={editingId === item.id}
                                            busy={writing}
                                            onTick={handleTick}
                                            onEdit={(row) => setEditingId(row.id)}
                                            onCancel={() => setEditingId(null)}
                                            onSave={handleSave}
                                            onRemove={handleRemove}
                                        />
                                    ))}
                                </ul>
                            </section>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

function BackLink() {
    return (
        <Link
            to="/shopping-lists"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-text-blue hover:underline"
        >
            <ArrowLeft className="size-4" aria-hidden="true" />
            Shopping lists
        </Link>
    );
}

const ShoppingListDetail = () => (
    <FeatureGate feature={Feature.SHOPPING_LISTS} fallback={<ShoppingListsLockedPage />}>
        <ShoppingListDetailContent />
    </FeatureGate>
);

export default ShoppingListDetail;
